import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleAuth = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || "Login failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left panel */}
      <div className="hidden lg:block relative bg-[#0B132B] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1564631211140-d918ad9b66d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxkZWFsZXJzaGlwJTIwaW50ZXJpb3IlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzg1MTgyODExfDA&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
          alt=""
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B132B] via-[#0B132B]/85 to-[#1C2541]/70" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <BrandLogo size="md" variant="light" />
          <div className="max-w-md">
            <div className="inline-block px-2 py-1 rounded-sm bg-[#D4AF37] text-[#0B132B] text-[10px] uppercase tracking-[0.25em] font-bold mb-6">
              For Agents & Agencies
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
              Turn any dealership lot into a signed proposal.
            </h1>
            <p className="text-white/70 text-base mt-6 leading-relaxed">
              Drop in a vehicle listing URL. We pull the photo, run the numbers,
              and lay out a presentation-ready quote — 24, 36, 48, 60, 72, 84 month
              terms, side-by-side.
            </p>
          </div>
          <div className="text-xs text-white/40 tracking-[0.18em] uppercase">
            © Advantage Warranty Group
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-12">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-6"
          data-testid="login-form"
        >
          <div className="lg:hidden">
            <BrandLogo size="md" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Sign in
            </div>
            <h2 className="font-display text-3xl font-black text-[#0B132B] mt-2">
              Welcome back
            </h2>
            <p className="text-sm text-[#6C757D] mt-2">
              Access your proposal workspace.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                placeholder="you@agency.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-[11px] font-semibold text-[#0B132B] hover:text-[#D4AF37] tracking-[0.06em]"
                  data-testid="login-forgot-password-link"
                >
                  Forgot?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {err && (
            <div className="text-sm text-red-600" data-testid="login-error">
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold transition-colors"
            data-testid="login-submit-button"
          >
            {loading ? "Signing in…" : "Sign in"}
            <ArrowRight size={16} className="ml-1.5" />
          </Button>

          <div className="relative flex items-center py-1">
            <div className="flex-grow border-t border-[#0B132B]/10" />
            <span className="mx-3 text-[10px] uppercase tracking-[0.28em] text-[#6C757D] font-bold">
              or
            </span>
            <div className="flex-grow border-t border-[#0B132B]/10" />
          </div>

          <Button
            type="button"
            onClick={startGoogleAuth}
            variant="outline"
            className="w-full h-11 rounded-sm border-[#0B132B]/20 hover:border-[#D4AF37] hover:bg-[#F8F9FA] text-[#0B132B] font-semibold transition-colors"
            data-testid="login-google-button"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" className="mr-2" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            Sign in with Google
          </Button>

          <p className="text-sm text-[#6C757D]">
            New agent?{" "}
            <Link
              to="/request-access"
              className="text-[#0B132B] font-semibold hover:text-[#D4AF37] transition-colors"
              data-testid="login-request-access-link"
            >
              Request access
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
